import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getOrders } from '@services/admin.service'
import './Orders.scss'

const STATUS_OPTIONS = ['', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled']
const PAYMENT_OPTIONS = ['', 'pending', 'paid', 'failed', 'refunded']

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatMoney(amount) {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(amount ?? 0)
}

export default function AdminOrders() {
  const navigate = useNavigate()
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 20 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getOrders({ status: status || undefined, paymentStatus: paymentStatus || undefined, page, pageSize: 20 })
      const payload = res?.data ?? res
      setData({
        items: payload?.items ?? [],
        total: payload?.total ?? 0,
        page: payload?.page ?? 1,
        pageSize: payload?.pageSize ?? 20,
      })
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Eroare la încărcare')
      setData({ items: [], total: 0, page: 1, pageSize: 20 })
    } finally {
      setLoading(false)
    }
  }, [status, paymentStatus, page])

  useEffect(() => {
    load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize))

  return (
    <div className="admin-page">
      <div className="admin-card orders-toolbar">
        <div className="orders-toolbar__filters">
          <label>
            Status
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o || 'all'} value={o}>{o || 'Toate'}</option>
              ))}
            </select>
          </label>
          <label>
            Plată
            <select value={paymentStatus} onChange={(e) => { setPaymentStatus(e.target.value); setPage(1) }}>
              {PAYMENT_OPTIONS.map((o) => (
                <option key={o || 'all'} value={o}>{o || 'Toate'}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <p className="admin-page__error">{error}</p>}
      {loading ? (
        <p>Se încarcă...</p>
      ) : (
        <div className="admin-card">
          <table className="admin-table orders-table">
            <thead>
              <tr>
                <th>Nr. comandă</th>
                <th>Client</th>
                <th>Data</th>
                <th>Total</th>
                <th>Status</th>
                <th>Plată</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr>
                  <td colSpan={6}>Nicio comandă găsită.</td>
                </tr>
              ) : (
                data.items.map((o) => (
                  <tr key={o.id} className="orders-table__row" onClick={() => navigate(`/admin/orders/${o.id}`)}>
                    <td>{o.order_number}</td>
                    <td>{[o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ')}</td>
                    <td>{formatDate(o.created_at)}</td>
                    <td>{formatMoney(o.total_amount)}</td>
                    <td><span className={`admin-badge status-${o.status}`}>{o.status}</span></td>
                    <td><span className={`admin-badge status-${o.payment_status}`}>{o.payment_status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="orders-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Înapoi</button>
              <span>Pagina {page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Înainte</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
